import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Check, X, Users, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from './ui/popover';
import ResponsiveTooltip from './ui/ResponsiveTooltip';
import { userAPI } from '../Services/apiService';

/**
 * Multi-select autocomplete user selection component
 * Supports both local filtering (from users prop) and backend search (via API)
 * @param {Object} props
 * @param {Array} props.users - Array of user objects (used for initial list and when backend search is disabled)
 * @param {Array} props.value - Array of selected user IDs (as strings or numbers)
 * @param {Function} props.onChange - Callback when selection changes (receives array of IDs)
 * @param {string} props.placeholder - Placeholder text
 * @param {React.ReactNode} props.icon - Icon component to display
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {boolean} props.enableBackendSearch - Enable backend search via API (default: false)
 * @param {Object} props.filterOptions - Additional filter options for backend search (e.g., { include_vendors: true })
 */
const UserMultiSelect = ({
  users = [],
  value = [],
  onChange,
  placeholder = "Select assignees...",
  icon: Icon,
  className = "",
  disabled = false,
  enableBackendSearch = false,
  filterOptions = {},
  dropdownWidth
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [backendUsers, setBackendUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [searchCache, setSearchCache] = useState({});
  const [selectedUsersCache, setSelectedUsersCache] = useState([]);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Ensure value is always an array of strings for consistency
  const selectedIds = Array.isArray(value) ? value.map(id => String(id)) : [];

  // Get selected users - merge from both sources and deduplicate by ID
  const getAllUsers = () => {
    if (!enableBackendSearch) return users;
    // Merge and deduplicate by ID
    const userMap = new Map();
    users.forEach(user => userMap.set(user.id, user));
    backendUsers.forEach(user => userMap.set(user.id, user));
    return Array.from(userMap.values());
  };
  const allUsers = getAllUsers();

  // Update selected users cache whenever we see new user objects for selected IDs
  useEffect(() => {
    if (selectedIds.length === 0) {
      if (selectedUsersCache.length > 0) setSelectedUsersCache([]);
      return;
    }

    const currentSelectedInPool = allUsers.filter(user => selectedIds.includes(String(user.id)));

    // Create Map for efficient merging
    const cacheMap = new Map();
    selectedUsersCache.forEach(user => cacheMap.set(String(user.id), user));

    let hasChanges = false;
    currentSelectedInPool.forEach(user => {
      const idStr = String(user.id);
      if (!cacheMap.has(idStr) || JSON.stringify(cacheMap.get(idStr)) !== JSON.stringify(user)) {
        cacheMap.set(idStr, user);
        hasChanges = true;
      }
    });

    // Remove users from cache that are no longer in selectedIds
    for (const [id] of cacheMap) {
      if (!selectedIds.includes(id)) {
        cacheMap.delete(id);
        hasChanges = true;
      }
    }

    if (hasChanges) {
      setSelectedUsersCache(Array.from(cacheMap.values()));
    }
  }, [allUsers, selectedIds]);

  // Use the cache for display, falling back to allUsers if not in cache yet
  const selectedUsers = selectedIds.map(id => {
    const cached = selectedUsersCache.find(u => String(u.id) === id);
    if (cached) return cached;
    return allUsers.find(u => String(u.id) === id) || { id, full_name: 'Loading...', email: '' };
  });

  // Backend search function with debouncing and caching
  const fetchUsersFromBackend = useCallback(async (searchQuery = '') => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    // Check cache first
    const cacheKey = searchQuery.toLowerCase().trim();
    if (searchCache[cacheKey]) {
      setBackendUsers(searchCache[cacheKey]);
      return;
    }

    // Debounce the search - wait 300ms after user stops typing
    return new Promise((resolve) => {
      searchTimeoutRef.current = setTimeout(async () => {
        setIsLoadingUsers(true);
        try {
          const params = {
            search: searchQuery.trim() || undefined,
            limit: 50, // Top 50 results as requested
            include_vendors: filterOptions.include_vendors !== false, // Default to true
            ...filterOptions
          };

          // Remove undefined values
          Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

          const response = await userAPI.getUsers(params, {
            signal: abortControllerRef.current.signal
          });

          const fetchedUsers = response.data || [];

          // Update cache
          setSearchCache(prev => ({
            ...prev,
            [cacheKey]: fetchedUsers
          }));

          setBackendUsers(fetchedUsers);
          resolve(fetchedUsers);
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Failed to fetch users:', error);
            setBackendUsers([]);
          }
        } finally {
          setIsLoadingUsers(false);
        }
      }, 300);
    });
  }, [filterOptions, searchCache]);

  // Fetch initial users when backend search is enabled and modal opens
  useEffect(() => {
    if (enableBackendSearch && open) {
      // If search value exists, search immediately; otherwise load initial set
      if (searchValue.trim()) {
        fetchUsersFromBackend(searchValue);
      } else {
        // Load initial users without search query (first 50)
        fetchUsersFromBackend('');
      }
    }
  }, [enableBackendSearch, open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Determine which users to use for filtering
  const usersToFilter = enableBackendSearch ? backendUsers : users;

  // Filter users based on search (only if backend search is disabled)
  const filteredUsers = enableBackendSearch
    ? usersToFilter // Backend already filtered, no need to filter again
    : users.filter((user) => {
      if (!searchValue) return true;
      const searchLower = searchValue.toLowerCase();
      const fullName = (user.full_name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    setOpen(true);

    // If backend search is enabled, fetch users from API
    if (enableBackendSearch) {
      fetchUsersFromBackend(newValue);
    }
  };

  const handleSelectUser = (user) => {
    const userId = String(user.id);
    let newSelection;

    // Toggle selection
    if (selectedIds.includes(userId)) {
      newSelection = selectedIds.filter(id => id !== userId);
    } else {
      newSelection = [...selectedIds, userId];
    }

    onChange(newSelection.map(id => parseInt(id))); // Convert back to numbers
    setSearchValue('');
    inputRef.current?.focus();
  };

  const handleRemoveUser = (userId) => {
    const newSelection = selectedIds.filter(id => id !== String(userId));
    onChange(newSelection.map(id => parseInt(id)));
  };

  const handleClearAll = () => {
    onChange([]);
    setSearchValue('');
  };

  // Show compact view when more than 2 users are selected
  const showCompactView = selectedUsers.length > 2;

  return (
    <div className={cn("relative w-full", className)}>
      <Popover open={open && !disabled} onOpenChange={(isOpen) => !disabled && setOpen(isOpen)}>
        <PopoverAnchor asChild>
          <div className="relative">
            <div className={cn(
              "min-h-[40px] w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background",
              "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
              disabled && "bg-gray-100 cursor-not-allowed opacity-60"
            )}>
              <div className="flex flex-wrap gap-1.5 items-center min-h-[24px]">
                {Icon && (
                  <Icon className={cn(
                    "h-4 w-4 shrink-0 mt-0.5",
                    disabled ? "text-gray-300" : "text-gray-400"
                  )} />
                )}

                {/* Selected users as badges - show first 2, then count */}
                {!showCompactView ? (
                  selectedUsers.map((user) => (
                    <Badge
                      key={user.id}
                      variant="secondary"
                      className="text-xs  flex items-center  gap-1.5 px-2 py-0.5 h-6 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                    >
                      <span className="max-w-[100px] truncate font-medium">
                        {user.full_name || user.email}
                      </span>
                      {!disabled && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveUser(user.id);
                          }}
                          className="ml-0.5 hover:bg-blue-200  rounded-full p-0.5 transition-colors"
                        >
                          <X className="h-3 w-3 text-blue-600" />
                        </button>
                      )}
                    </Badge>
                  ))
                ) : (
                  <>
                    {/* Show first 2 badges */}
                    {selectedUsers.slice(0, 2).map((user) => (
                      <Badge
                        key={user.id}
                        variant="secondary"
                        className="text-xs flex items-center gap-1.5 px-2 py-0.5 h-6 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                      >
                        <span className="max-w-[80px]  truncate font-medium">
                          {user.full_name || user.email}
                        </span>
                        {!disabled && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveUser(user.id);
                            }}
                            className="ml-0.5 hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                          >
                            <X className="h-3 w-3 text-blue-600" />
                          </button>
                        )}
                      </Badge>
                    ))}
                    {/* Show count badge - clickable and with tooltip */}
                    <ResponsiveTooltip
                      content={
                        <div className="space-y-1 ">
                          <div className="text-xs font-semibold text-gray-700 mb-1">
                            Additional Assignees ({selectedUsers.length - 2}):
                          </div>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {selectedUsers.slice(2).map((user) => (
                              <div
                                key={user.id}
                                className="text-xs border-b border-gray-200 text-gray-600 py-0.5 px-1 rounded hover:bg-gray-100"
                              >
                                {user.full_name || user.email}
                              </div>
                            ))}
                          </div>
                        </div>
                      }
                      side="top"
                      className="max-w-xs p-2 bg-white border border-gray-200"
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!disabled) {
                            setOpen(true);
                            inputRef.current?.focus();
                          }
                        }}
                        className={cn(
                          "text-xs px-2 py-0.5 h-6 bg-blue-100 text-blue-700 border border-blue-300 font-semibold rounded-md",
                          "hover:bg-blue-200 hover:border-blue-400 transition-colors cursor-pointer",
                          "flex items-center gap-1",
                          disabled && "cursor-not-allowed opacity-60"
                        )}
                      >
                        +{selectedUsers.length - 2} more
                      </button>
                    </ResponsiveTooltip>
                  </>
                )}

                {/* Input for searching */}
                <Input
                  ref={inputRef}
                  type="text"
                  value={searchValue}
                  onChange={handleInputChange}
                  onFocus={() => !disabled && setOpen(true)}
                  placeholder={selectedUsers.length === 0 ? placeholder : 'Search...'}
                  className="flex-1 min-w-[80px] border-0 p-0 h-6 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm"
                  autoComplete="off"
                  disabled={disabled}
                />

                {/* Clear all button */}
                {selectedUsers.length > 0 && !disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearAll();
                    }}
                    className="ml-auto p-1 hover:bg-gray-100 rounded-full transition-colors shrink-0"
                    title="Clear all"
                  >
                    <X className="h-4 w-4 text-gray-400" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </PopoverAnchor>

        {open && !disabled && (
          <PopoverContent
            className="p-0"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
            style={{ width: dropdownWidth || 'var(--radix-popover-trigger-width)' }}
          >
            <Command shouldFilter={false}>
              {/* Show selected users at top */}


              <CommandList className="max-h-[250px]  overflow-y-auto overscroll-contain"
                onWheel={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.scrollTop += e.deltaY;
                }}
              >
                {isLoadingUsers ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    <span className="ml-2 text-sm text-gray-500">Searching...</span>
                  </div>
                ) : (
                  <>
                    <CommandEmpty>
                      {enableBackendSearch && searchValue.trim()
                        ? `No users found matching "${searchValue}"`
                        : 'No user found.'}
                    </CommandEmpty>
                    {filteredUsers.length > 0 && (
                      <CommandGroup>
                        {filteredUsers.map((user) => {
                          const isSelected = selectedIds.includes(String(user.id));
                          return (
                            <CommandItem
                              key={user.id}
                              value={`${user.full_name} ${user.email} ${user.id}`}
                              onSelect={() => {
                                handleSelectUser(user);
                                setOpen(true);            // keep popover open for multi-select
                                inputRef.current?.focus();// refocus input
                              }}
                              className="px-2 "
                            >
                              <div className="flex items-center gap-2 w-full">
                                <div className={cn(
                                  "h-4 w-4 shrink-0 border rounded flex items-center justify-center",
                                  isSelected ? "bg-primary border-primary" : "border-gray-300"
                                )}>
                                  {isSelected && <Check className="h-3 w-3 text-white" />}
                                </div>
                                {Icon && <Icon className="h-3.5 w-3.5 text-gray-500 shrink-0" />}
                                <div className="flex flex-col flex-1 min-w-0">
                                  <span className="text-sm font-medium truncate">
                                    {user.full_name || user.email}
                                  </span>
                                  {user.full_name && (
                                    <span className="text-xs text-gray-500 truncate">{user.email}</span>
                                  )}
                                </div>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    )}
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        )}
      </Popover>

      {/* Helper text showing count - only show if not in compact view */}
      {selectedUsers.length > 0 && !showCompactView && (
        <div className="text-xs text-gray-500 mt-1.5 px-1">
          {selectedUsers.length} assignee{selectedUsers.length !== 1 ? 's' : ''} selected
        </div>
      )}
    </div>
  );
};

export default UserMultiSelect;

